import { PING_INTERVAL, usePingInterval } from "@/auth/device-tracking-session";
import { useAuth } from "@/auth/index";
import { ENV } from "@/env";
import { Configuration, SessionApi } from "@mm/metx-workbench.meteomatics.com";
import React, { createContext, useContext, useState, type ReactNode, useCallback, useEffect } from "react";

interface AppSessionContextProps {
  sessionActivated: boolean;
  invalid: boolean;
  activateSession: () => Promise<void>;
  disableSession: () => void;
}

const AppSessionContext = createContext<AppSessionContextProps | undefined>(undefined);

export const AppSessionProvider = ({ children }: { children: ReactNode }) => {
  const { session, fetchToken } = useAuth();
  const [sessionActivated, setSessionActivated] = useState(false);
  const [invalid, setInvalid] = useState(false);

  const accessToken = session?.token?.access_token || null;
  const sessionApi = accessToken ? new SessionApi(new Configuration({ basePath: ENV.baseUrlMetx, accessToken })) : null;
  useEffect(() => {
    if (accessToken) {
      activateSession();
    }
  }, [accessToken]);
  const activateSession = useCallback(async () => {
    if (!sessionApi) {
      console.warn("No sessionApi available to activate session.");
      return;
    }
    try {
      await sessionApi.v1UpdateSession();
      setInvalid(false);
      setSessionActivated(true);
    } catch (err) {
      console.error("Failed to activate session:", err);
      setInvalid(true);
    }
  }, [sessionApi]);

  const disableSession = useCallback(() => {
    setInvalid(true);
  }, []);

  // Ping logic:
  const { result, start } = usePingInterval(PING_INTERVAL);

  useEffect(() => {
    if (sessionActivated) {
      start();
    }
  }, [sessionActivated, start]);

  useEffect(() => {
    if (!result) {
      return;
    }
    if (result.status === 403) {
      console.log("Silent renew");
      // Try silent renew token
      fetchToken();
    }
    // status 460 means invalid session
    if (result.status === 460) {
      console.warn("Session invalid");
      setInvalid(true);
    }
  }, [result]);

  return (
    <AppSessionContext.Provider value={{ sessionActivated, invalid, activateSession, disableSession }}>
      {children}
    </AppSessionContext.Provider>
  );
};

export const useAppSession = () => {
  const context = useContext(AppSessionContext);
  if (!context) {
    throw new Error("useAppSession must be used within an AppSessionProvider");
  }
  return context;
};
